// React context that surfaces the host-supplied `VoicePlaybackDriver`
// to the shared chat tree. Hosts wrap their chat surface with this
// provider; the shared `useVoicePlayback` hook consumes the driver
// through `useContext` and falls back to a no-op when no provider is
// mounted (keeps unit tests + storybooks rendering without a driver).

import React, { createContext, useContext } from 'react';
import type { VoicePlaybackDriver } from './voicePlaybackDriver';

const VoicePlaybackContext = createContext<VoicePlaybackDriver | null>(null);

export interface VoicePlaybackProviderProps {
  readonly driver: VoicePlaybackDriver;
  readonly children: React.ReactNode;
}

export function VoicePlaybackProvider({
  driver,
  children,
}: VoicePlaybackProviderProps): JSX.Element {
  return (
    <VoicePlaybackContext.Provider value={driver}>
      {children}
    </VoicePlaybackContext.Provider>
  );
}

/**
 * Returns the host-injected `VoicePlaybackDriver`, or `null` when the
 * surrounding tree has no provider. Callers must gate any playback
 * work on a non-null return so the shared bubble degrades to its
 * existing no-op `onTogglePlay` behaviour outside provider scope.
 */
export function useVoicePlaybackDriver(): VoicePlaybackDriver | null {
  return useContext(VoicePlaybackContext);
}
